import React from 'react';
import { useNavigate } from 'react-router-dom';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { BellIcon } from 'lucide-react';
import { useAcademyHub } from '../../context/AcademyHub';
import { timeAgo } from '../../utils/academyFormat';
export function NotificationMenu() {
  const {
    visibleNotifications: notifications,
    markRead,
    markAllRead
  } = useAcademyHub();
  const navigate = useNavigate();
  const reduced = useReducedMotion();
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef<HTMLDivElement>(null);
  const unread = notifications.filter(n => !n.read).length;
  React.useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);
  return <div ref={ref} className="relative" data-id="element-464" data-mp="t79mgakkoqdw">
      <button type="button" onClick={() => setOpen(o => !o)} aria-expanded={open} aria-label={unread ? `Notifications, ${unread} unread` : 'Notifications'} className="relative flex h-10 w-10 items-center justify-center rounded-sm text-body transition-colors duration-150 hover:bg-panel hover:text-ink" data-id="element-465" data-mp="16x3cuxbq7ipq">
        <BellIcon className="h-5 w-5" aria-hidden="true" data-id="element-466" data-mp="1bvob4r7f3sbj" />
        {unread > 0 && <span className="absolute right-1 top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-gold px-1 text-[10px] font-semibold text-charcoal" data-id="element-467" data-mp="1puo05zyue988">
            {unread > 9 ? '9+' : unread}
          </span>}
      </button>
      <AnimatePresence data-id="element-468" data-mp="3rp5ec0t4u31t">
        {open && <motion.div initial={reduced ? false : {
        opacity: 0,
        y: -4,
        scale: 0.98
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={reduced ? undefined : {
        opacity: 0,
        y: -4,
        scale: 0.98
      }} transition={{
        duration: 0.16,
        ease: [0.23, 1, 0.32, 1]
      }} className="absolute right-0 top-12 z-40 w-[min(360px,calc(100vw-2.5rem))] origin-top-right overflow-hidden rounded-md border border-line bg-panel shadow-2xl" data-id="element-469" data-mp="3nmsbbk5rsj8q">
            <div className="flex items-center justify-between border-b border-line px-4 py-3" data-id="element-470" data-mp="29ki7wb4jml60">
              <p className="text-[14px] font-medium text-ink" data-id="element-471" data-mp="2eis1wiej7fq1">Notifications</p>
              {unread > 0 && <button type="button" onClick={markAllRead} className="text-small text-royal hover:text-ink" data-id="element-472" data-mp="2j9i8fyyp1omb">
                  Mark all read
                </button>}
            </div>
            {notifications.length ? <ul className="max-h-[400px] divide-y divide-line overflow-y-auto" data-id="element-473" data-mp="2t9sorsg0zc5p">
                {notifications.map(n => <li key={n.id} data-id="element-474" data-mp="lm7p36vtqie3">
                    <button type="button" onClick={() => {
              markRead(n.id);
              setOpen(false);
              if (n.link) navigate(n.link);
            }} className="flex w-full items-start gap-3 px-4 py-3 text-left transition-colors duration-150 hover:bg-charcoal" data-id="element-475" data-mp="lmg0eucatfn6">
                      <span className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${n.read ? 'bg-transparent' : 'bg-gold'}`} aria-hidden="true" data-id="element-476" data-mp="158vnhtivn9gs" />
                      <span className="min-w-0" data-id="element-477" data-mp="10dgcrag61tol">
                        <span className={`block text-[14px] ${n.read ? 'text-body' : 'text-ink'}`} data-id="element-478" data-mp="vjc48mr95dle">{n.title}</span>
                        {n.body && <span className="mt-0.5 block truncate text-small text-muted" data-id="element-479" data-mp="ql0u0ax1p12n">{n.body}</span>}
                        <span className="mt-1 block text-[12px] text-muted" data-id="element-480" data-mp="1o1zjvmnhc0f3">{timeAgo(n.at)}</span>
                      </span>
                    </button>
                  </li>)}
              </ul> : <p className="px-4 py-8 text-center text-[14px] text-muted" data-id="element-481" data-mp="17tju07o18u7d">Nothing yet. Enrol in a course to get started.</p>}
          </motion.div>}
      </AnimatePresence>
    </div>;
}